import { motion, AnimatePresence } from 'framer-motion'
import { Lightbulb, X } from 'lucide-react'
import { Button } from '@/components/common'

/**
 * Auto-prompted modal that appears when every branch has terminated or
 * been pruned (Round model v2). Asks the user whether to synthesize the
 * problem statement now via OAI.
 */
export interface GenerateConfirmModalProps {
  isOpen: boolean
  branchSummary: { active: number; completed: number; pruned: number }
  onCancel: () => void
  onConfirm: () => void | Promise<void>
}

export const GenerateConfirmModal = ({
  isOpen,
  branchSummary,
  onCancel,
  onConfirm,
}: GenerateConfirmModalProps) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
          onClick={onCancel}
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={onCancel}
              aria-label="Close"
              className="absolute top-3 right-3 p-1 rounded text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                <Lightbulb className="w-5 h-5 text-amber-600 dark:text-amber-300" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900 dark:text-white text-lg">
                  Ready to synthesize?
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                  All branches are either terminated or pruned. Generate the
                  AI3C problem statement now, or keep exploring by spawning
                  more branches manually.
                </p>
                <ul className="mt-3 text-xs text-slate-500 dark:text-slate-400 space-y-0.5">
                  <li>{branchSummary.completed} branch(es) completed</li>
                  <li>{branchSummary.pruned} branch(es) pruned</li>
                  <li>{branchSummary.active} branch(es) still active</li>
                </ul>
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-3">
              <Button variant="secondary" onClick={onCancel}>
                Not yet
              </Button>
              <Button
                onClick={() => void onConfirm()}
                leftIcon={<Lightbulb className="w-4 h-4" />}
              >
                Generate problem statement
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default GenerateConfirmModal
